import React from 'react';

type SkeletonProps = {
  className?: string;
  style?: React.CSSProperties;
};

export const SkeletonBlock: React.FC<SkeletonProps> = ({ className = '', style }) => (
  <div className={`shimmer rounded ${className}`} style={{ backgroundColor: '#f0f0f0', ...style }} />
);

export const SkeletonText: React.FC<SkeletonProps & { lines?: number; lineClassName?: string }>
  = ({ lines = 3, className = 'space-y-2', lineClassName = 'h-4', style }) => (
  <div className={className} style={style}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBlock key={i} className={lineClassName} />
    ))}
  </div>
);

export const SkeletonAvatar: React.FC<SkeletonProps & { size?: number }>
  = ({ size = 40, className = '' }) => (
  <div className={`shimmer rounded-full ${className}`} style={{ width: size, height: size, backgroundColor: '#f0f0f0' }} />
);

export const SkeletonCard: React.FC<SkeletonProps & { withHeader?: boolean; withFooter?: boolean }>
  = ({ className = '', withHeader = true, withFooter = false }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 ${className}`}>
    {withHeader && (
      <div className="flex items-center gap-4 mb-4">
        <SkeletonAvatar />
        <div className="flex-1">
          <SkeletonBlock className="h-4 w-1/3 mb-2" />
          <SkeletonBlock className="h-3 w-1/4" />
        </div>
      </div>
    )}
    <SkeletonText lines={4} />
    {withFooter && (
      <div className="mt-4 grid grid-cols-3 gap-3">
        <SkeletonBlock className="h-8 rounded-lg" />
        <SkeletonBlock className="h-8 rounded-lg" />
        <SkeletonBlock className="h-8 rounded-lg" />
      </div>
    )}
  </div>
);

export const SkeletonTable: React.FC<SkeletonProps & { rows?: number; cols?: number }>
  = ({ rows = 6, cols = 6, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${className}`}>
    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
      <SkeletonBlock className="h-5 w-32" />
    </div>
    <div className="divide-y divide-gray-100">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid grid-cols-12 gap-4 px-6 py-4">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonBlock key={c} className="h-4 col-span-2" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const PageHeaderSkeleton: React.FC = () => (
  <div className="flex items-center gap-4 mb-6">
    <SkeletonAvatar size={56} />
    <div className="flex-1">
      <SkeletonBlock className="h-6 w-48 mb-2" />
      <SkeletonBlock className="h-4 w-72" />
    </div>
  </div>
);

export const ListItemSkeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 ${className}`}>
    <SkeletonAvatar />
    <div className="flex-1">
      <SkeletonBlock className="h-4 w-2/3 mb-2" />
      <SkeletonBlock className="h-3 w-1/3" />
    </div>
    <SkeletonBlock className="h-6 w-16 rounded-full" />
  </div>
);

export const ChartSkeleton: React.FC<SkeletonProps> = ({ className = 'h-64 bg-gray-50 rounded-2xl p-4 border border-gray-100' }) => (
  <div className={className}>
    <SkeletonBlock className="h-5 w-40 mb-4" />
    <div className="grid grid-cols-12 gap-2 h-40 items-end">
      {Array.from({ length: 12 }).map((_, i) => (
        <SkeletonBlock key={i} className={i % 3 === 0 ? 'h-24' : 'h-16'} />
      ))}
    </div>
  </div>
);

export const FullPageSkeleton: React.FC = () => (
  <div className="min-h-full">
    <div className="py-6">
      <PageHeaderSkeleton />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <ChartSkeleton />
      <div className="mt-6">
        <SkeletonTable rows={5} cols={6} />
      </div>
    </div>
  </div>
);

export default SkeletonBlock;




